import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';

export interface StartAllocationScopeUser {
    user_id: number;
    name: string;
    profile_type: string | null;
}

export interface StartAllocationScopeData {
    role: string;
    roleLabel: string;
    users: StartAllocationScopeUser[];
    /** Pula a pergunta "só esta função ou outras" — usado quando não há função de origem (ex.: botão "Novo turno"). */
    forceCrewMode?: boolean;
}

export type StartAllocationScopeResult =
    | { mode: 'cancel' }
    | { mode: 'role' }
    | { mode: 'crew'; users: StartAllocationScopeUser[] };

interface ScopeUserOption extends StartAllocationScopeUser {
    selected: boolean;
}

interface ScopeRoleGroup {
    role: string;
    label: string;
    users: ScopeUserOption[];
}

const ROLE_LABELS: Record<string, string> = {
    DRIVER: 'Motorista',
    NURSE: 'Enfermeiro',
    PARAMEDIC: 'Técnico de Enfermagem',
    NURSING_TECH: 'Técnico de Enfermagem',
    DOCTOR: 'Médico',
    OTHER: 'Grupo',
};

const ROLE_ORDER = ['DRIVER', 'NURSE', 'PARAMEDIC', 'NURSING_TECH', 'DOCTOR', 'OTHER'];

@Component({
    selector: 'app-start-allocation-scope-dialog',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        MatButtonModule,
        MatCheckboxModule,
        MatIconModule,
        AutoTranslateDirective,
        AutoTranslatePipe,
    ],
    templateUrl: './start-allocation-scope-dialog.component.html',
    styleUrls: ['./start-allocation-scope-dialog.component.scss'],
})
export class StartAllocationScopeDialogComponent {
    scope: 'role' | 'crew' = 'role';
    readonly groups: ScopeRoleGroup[];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: StartAllocationScopeData,
        private dialogRef: MatDialogRef<StartAllocationScopeDialogComponent, StartAllocationScopeResult>,
    ) {
        this.dialogRef.addPanelClass('card-dialog');
        this.groups = this.buildGroups(data.users || []);
        if (data.forceCrewMode) this.scope = 'crew';
    }

    get selectedUsers(): StartAllocationScopeUser[] {
        return this.groups.flatMap((g) => g.users.filter((u) => u.selected));
    }

    get canContinue(): boolean {
        return this.scope === 'role' || this.selectedUsers.length > 0;
    }

    selectScope(scope: 'role' | 'crew'): void {
        this.scope = scope;
    }

    cancel(): void {
        this.dialogRef.close({ mode: 'cancel' });
    }

    submit(): void {
        if (!this.canContinue) return;
        if (this.scope === 'role') {
            this.dialogRef.close({ mode: 'role' });
            return;
        }
        this.dialogRef.close({
            mode: 'crew',
            users: this.selectedUsers.map((u) => ({
                user_id: u.user_id,
                name: u.name,
                profile_type: u.profile_type,
            })),
        });
    }

    private buildGroups(users: StartAllocationScopeUser[]): ScopeRoleGroup[] {
        const byRole = new Map<string, ScopeUserOption[]>();
        for (const user of users) {
            const role = user.profile_type || 'OTHER';
            const list = byRole.get(role) ?? [];
            list.push({ ...user, selected: false });
            byRole.set(role, list);
        }
        const roles = [...byRole.keys()].sort((a, b) => {
            const ai = ROLE_ORDER.indexOf(a);
            const bi = ROLE_ORDER.indexOf(b);
            return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
        });
        return roles.map((role) => ({
            role,
            label: ROLE_LABELS[role] ?? role,
            users: (byRole.get(role) || []).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
        }));
    }
}
